import { consume } from '@lit/context'
import { LitElement, PropertyValues, css, html } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import type { EPNavList, EPNavListContext } from './ep-nav-list'
import { EPNavListSubNav, context } from './ep-nav-list'
import { generateUUID } from '../../functions/generateUUID'

import CHEVRON_SVG from '../../shared-assets/chevron_up.svg'

export type EPNavListItemVariant = 'plain' | 'button';
export type EPNavListItemAppearance = 'normal' | 'destructive'

/**
 * A nav list
 */
@customElement('ep-nav-list-item')
export class EPNavListItem extends LitElement {

  @property({ attribute: false })
  id: string = generateUUID();

  @consume({ context: context, subscribe: true })
  @property({ attribute: false })
  provider?: EPNavListContext;

  @property({ type: Boolean, reflect: true })
  selected: boolean = false;

  @property({ type: Boolean, reflect: true })
  disabled: boolean = false;

  @property({ reflect: true })
  value: string = '';

  @property({ reflect: true })
  open?: boolean = undefined;

  @property({ reflect: true })
  href?: string;

  @property({ reflect: true })
  target?: string;

  @property({ type: String, reflect: true })
  appearance: EPNavListItemAppearance = 'normal'

  @property({ type: String, reflect: true })
  variant: EPNavListItemVariant = 'button'

  @property({ type: Boolean, attribute: 'text-select', reflect: true })
  textSelectable: boolean = false;

  get rootNavList(): EPNavList | undefined {
    return this.provider?.rootNavList;
  }

  get hasSubNav() {
    return this.open !== undefined;
  }

  @query('slot[name=subnav]')
  subNavSlotElement?: HTMLSlotElement;

  render() {
    return html`
      <li part="content"
        data-selected=${this.selected}
        data-open=${this.open ?? ''}
        data-text-select=${this.textSelectable}
        data-appearance=${this.appearance}
        data-variant=${this.variant}
        data-disabled=${this.disabled}
        @click=${this._handleClick}>
        <a href=${ifDefined(this.href)} target=${ifDefined(this.target)}>
          <div class="divider-container">
            <slot name="leading" part="leading"></slot>
            <span part="content">
              <slot></slot>
              <slot name="description"></slot>
            </span>
            <span part="trailing">
              <slot name="trailing"></slot>
              ${
                this.open === undefined
                ? ''
                : html`
                <img class="chevron" src=${CHEVRON_SVG} style="transform: ${this.open === true ? 'rotate(0deg)' : 'rotate(180deg)'};" />
                `
              }  
            </span>
          </div>
        </a>
      </li>
      <slot part="subnav" name="subnav" data-open=${this.open ?? ''} @slotchange=${this._handleSubNavSlotChange}></slot>
    `
  }

  // #region Related to opening and closing the subnav, if applicable
  private _handleClick(event: MouseEvent) {
    if (this.disabled) {
      event.stopPropagation()
    }
    if (this.hasSubNav) {
      this.open = !this.open;
    }
    else {
      this._dispatchSelectEvent()
    }
  }

  private _handleSubNavSlotChange() {
    let hasSubNav = this.subNavSlotElement?.assignedElements().some(e => e instanceof EPNavListSubNav) ?? false;
    this.open = (
      hasSubNav
      ? (
        this.provider?.rootNavList.subNavStartClosed === true
        ? false
        : true
      )
      : this.open
    );
  }
  // #endregion

  // #region Related to "selecting" the item itself
  private _dispatchSelectEvent() {
    if (this.provider?.rootNavList.selectable === true && this.provider.selectedItemID !== this.id) {
      //console.log('dispatching ep-nav-list-select', this);
      const event = new CustomEvent<EPNavListItem>('ep-nav-list-select', { 
        bubbles: true,
        composed: true,
        detail: this,
      })
      this.dispatchEvent(event);
    }
  }

  protected updated(_changedProperties: PropertyValues<this>) {
    super.updated(_changedProperties)
    for(let [prop, _] of _changedProperties) {
      switch(prop) {
        // If our provider changed, make sure our state is correct
        case 'provider': {
          if (this.provider?.selectable) {
            this.selected = (this.provider?.selectedItemID === this.id);
          }
          break;
        }
        // If we changed, make sure our provider is correct
        case 'selected': {
          if (this.selected && this.provider?.rootNavList) {
            this._dispatchSelectEvent();
          }
          break;
        }
      }
    }
  }

  // #endregion

  static styles = css`
    :host {
      --font-prose: ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
      /* --color: var(--color-plain); */
      --indent-count-item: 0;
      --color-plain: rgb(36, 41, 47);
      --color-destructive: rgb(207, 34, 46);
      color: var(--color, var(--color-plain));
      display: block;
    }

    * {
      box-sizing: border-box;
    }

    li {
      position: relative;
      display: flex;
      padding: 0px;
      font-size: 14px;
      line-height: 20px;
      min-height: 5px;
      margin-left: 8px;
      margin-right: 8px;
      border-radius: 6px;
      transition: background 33.333ms linear 0s;
      color: var(--color);
      cursor: pointer;
      appearance: none;
      background: unset;
      border: unset;
      width: calc(100% - 16px);
      font-family: unset;
      font-weight: normal;
      text-align: unset;
      margin-top: unset;
      margin-bottom: unset;
    }

    li[data-variant=plain]:not([data-disabled=true]) {
      cursor: default;
    }

    li[data-disabled=true] {
      cursor: not-allowed;
      opacity: 0.5;
    }

    li[data-appearance=destructive] {
      --color: var(--color-destructive);
    }

    li[data-selected=true] {
      font-weight: 600;
      background-color: rgba(208, 215, 222, 0.24);
    }

    li[data-selected=true]::after {
      position: absolute;
      top: calc(50% - 12px);
      left: -8px;
      width: 4px;
      height: 24px;
      content: "";
      background-color: rgb(9, 105, 218);
      border-radius: 6px;
    }

    li[data-text-select=true] {
      user-select: initial;
    }

    li:not([data-text-select=true]) {
      user-select: none;
    }

    a {
      text-decoration: none;
      padding: 6px 8px;
      padding-left: calc(8px + (8px * 3 * var(--indent-count-item, 0)));
      display: flex;
      flex-grow: 1;
      border-radius: 6px;
      color: inherit;
      font-size: var(--override-font-size, inherit);
      font-weight: var(--override-font-weight, inherit);
      /* max-width: 100%; */
    }

    .divider-container {
      display: flex;
      flex-direction: row; /* used to be column */
      flex-grow: 1;
      min-width: 0px;
    }

    .divider-container::before {
      content: " ";
      display: block;
      position: absolute;
      width: 100%;
      top: -7px;
      border-width: 0px;
      border-style: solid;
      border-image: initial;
      border-color: var(--divider-color,transparent);
    }

    li:hover:not([aria-disabled]):not([data-disabled=true]):not([data-variant=plain]) {
      background-color: rgba(208, 215, 222, 0.32);
      --color: var(--color);
    }
    li[data-variant=button][data-appearance=destructive]:hover:not([aria-disabled]):not([data-disabled=true]):not([data-variant=plain]) {
      background-color: rgba(255, 235, 233, 0.64);
      --color: var(--color-destructive);
    }

    li[data-variant=button]:active:not([aria-disabled]):not([data-disabled=true]) {
      background-color: rgba(208, 215, 222, 0.48);
      --color: var(--color-plain);
    }
    li[data-variant=button][data-appearance=destructive]:active:not([aria-disabled]):not([data-disabled=true]) {
      background-color: rgba(255, 235, 233, 0.80);
      --color: var(--color-destructive);
    }

    li:hover:not([aria-disabled]) {
      --divider-color: transparent;
    }

    [part=content] {
      flex-grow: 1;
      /* max-width: 100%; */
      display: flex;
      flex-direction: column;
    }

    li[data-open=true] [part=content], li[data-open=false] [part=content] {
      cursor: pointer;
      font-weight: 600;
    }

    /* Only apply this style to the "leading" slot if it is slotted */
    ::slotted([slot=leading]) {
      height: 20px;
      min-width: 16px;
      max-width: 20px;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-shrink: 0;
      margin-right: 8px;
      color: rgb(87, 96, 106);
    }

    [part=trailing] {
      height: 20px;
      flex-shrink: 0;
      color: rgb(87, 96, 106);
      margin-left: 8px;
      font-weight: initial;
      display: inline-flex;
      gap: 4px;
      align-items: center;
    }

    [part=trailing] .chevron {
      width: 16px;
      height: 16px;
      transition: transform 250ms;
    }

    [part=subnav][data-open=false] {
      display: none;
    }

    ::slotted(ep-nav-list), ::slotted(ep-nav-list-subnav) {
      --indent-count-list: calc(var(--indent-count-item) + 1);
    }

    ::slotted([slot=description]) {
      font-size: 12px;
      line-height: 16px;
      flex-grow: 1;
      flex-basis: 0px;
      min-width: 0px;
      margin-left: 0;
      color: rgb(101, 109, 118);

      /* height: 20px;
      min-width: 16px;
      max-width: 20px;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-shrink: 0;
      margin-right: 8px;
      color: rgb(87, 96, 106); */
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-nav-list-item': EPNavListItem
  }
}
